/**
 * PURPOSE: Las acciones del Centro de Programación, en un solo sitio (FTR-0028 WG3).
 * RESPONSIBILITIES: Convertir una intención de la interfaz —guardar la edición inline, aplicar una
 *   jornada, materializar, reorganizar, retirar— en la llamada correcta, con la versión del borrador
 *   y el refresco que corresponde.
 *
 *   POR QUÉ AQUÍ Y NO EN CADA VISTA. Las cuatro vistas y los paneles disparan las MISMAS
 *   operaciones. Repartirlas haría que una vista olvidara el `If-Match`, otra refrescara de menos y
 *   una tercera inventara su propio mensaje de error. Una sola definición es también una sola
 *   oportunidad de equivocarse.
 *
 *   LO QUE NUNCA SE HACE AQUÍ: recalcular en silencio un plan entre la vista previa y su ejecución.
 *   `summaryHash` y `planHash` viajan tal cual llegaron del preview que el administrador vio; si el
 *   backend responde que cambiaron, se muestra y se recarga — nunca se reintenta solo.
 * COLLABORATORS: ProgrammingCenterProvider.jsx, services/programmingCenterService.js,
 *   utils/programmingCenterEdit.js.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/useProgrammingCenterActions.js
 */
import { useMemo } from "react";
import { toast } from "react-hot-toast";

import {
  adoptMatchIntoGraph,
  applyDraftSync,
  applyReorganization,
  assignParticipantToSlot,
  convertDraftMatchToManual,
  createManualMatch,
  deleteDraftMatch,
  materializeDraft,
  putProgrammingCenterDraft,
  recomputePhaseGraph,
  resolveParticipants,
  retireCenterMatch,
  scheduleBatch,
  updateGraphSource,
} from "../../../services/programmingCenterService.js";
import {
  applyScheduleChange,
  buildBulkScheduleEntries,
  buildChainedTimeEntries,
  buildJornadaEntries,
  describeScheduleResult,
} from "../../../utils/programmingCenterEdit.js";

export const useProgrammingCenterActions = ({
  execute,
  courtsByVenue,
  dirtyEntries,
  rows,
  selection,
  clearSelection,
}) =>
  useMemo(
    () => ({
      /** Cambio en el borrador local de edición inline, con la relación sede→cancha ya aplicada. */
      changeScheduleField: (current, row, field, value) => ({
        ...current,
        [row.iddraftmatch]: applyScheduleChange(current[row.iddraftmatch] || {}, field, value, {
          courtsByVenue,
        }),
      }),

      /** Guarda SOLO lo que cambió. Un lote vacío no se envía: no hay nada que confirmar. */
      saveInlineChanges: async () => {
        if (!dirtyEntries.length) {
          toast("No hay cambios de programación pendientes.");
          return { ok: false };
        }
        return execute(
          "schedule",
          ({ idevent, idsport, version }) =>
            scheduleBatch({ idevent, idsport, matches: dirtyEntries, version }),
          {
            successMessage: null,
          }
        ).then((result) => {
          if (result.ok) toast.success(describeScheduleResult(result.data));
          return result;
        });
      },

      /** El mismo valor a toda la selección. */
      applyBulk: async (changes) => {
        const built = buildBulkScheduleEntries({ rows, selection, changes });
        if (built.error) {
          toast.error(built.error);
          return { ok: false };
        }
        const result = await execute("schedule", ({ idevent, idsport, version }) =>
          scheduleBatch({ idevent, idsport, matches: built.entries, version })
        );
        if (result.ok) {
          toast.success(describeScheduleResult(result.data));
          clearSelection();
        }
        return result;
      },

      /** "Hora + N min": encadena horarios sobre la selección, en su orden de presentación. */
      applyChainedTime: async ({ startTime, stepMinutes }) => {
        const built = buildChainedTimeEntries({
          rows,
          selection,
          startTime,
          stepMinutes,
        });
        if (built.error) {
          toast.error(built.error);
          return { ok: false };
        }
        const result = await execute("schedule", ({ idevent, idsport, version }) =>
          scheduleBatch({ idevent, idsport, matches: built.entries, version })
        );
        if (result.ok) {
          toast.success(describeScheduleResult(result.data));
          clearSelection();
        }
        return result;
      },

      /**
       * "Aplicar a toda la jornada": fecha, hora y sede como campos INDEPENDIENTES. Cada uno se
       * escribe solo si se dio y lo que no se dio se conserva; la duración es el paso de cálculo de
       * la secuencia por sede y nunca un campo del partido. El ALCANCE es la jornada completa —nunca
       * solo lo que el filtro de pantalla deja visible—, igual que la semántica previa (FTR-0008).
       */
      applyJornada: async ({ dateNo, idtypephase, groupNo, date, startTime, stepMinutes, idsede }) => {
        const built = buildJornadaEntries({
          rows,
          dateNo,
          idtypephase,
          groupNo,
          date,
          startTime,
          stepMinutes,
          idsede,
        });
        if (built.error) {
          toast.error(built.error);
          return { ok: false };
        }
        const result = await execute("schedule", ({ idevent, idsport, version }) =>
          scheduleBatch({ idevent, idsport, matches: built.entries, version })
        );
        if (result.ok) {
          toast.success(describeScheduleResult(result.data));
          // Lo que NO se pudo aplicar se dice aparte: el lote fue correcto, la secuencia no existió.
          if (built.warning) toast(built.warning);
        }
        return result;
      },

      deleteRow: async (row) => {
        if (!window.confirm(`¿Quitar ${row.label} del borrador?`)) return { ok: false };
        return execute(
          "delete-row",
          ({ idevent, idsport, version }) =>
            deleteDraftMatch({ idevent, idsport, iddraftmatch: row.iddraftmatch, version }),
          { successMessage: "Fila eliminada del borrador." }
        );
      },

      /**
       * Retirar es `state = -2`, nunca un DELETE: el partido deja de ser del Centro y libera su
       * `match_key` para que la misma clave pueda volver si su configuración regresa.
       */
      retireMatch: async (row) => {
        if (
          !window.confirm(
            `¿Retirar el partido ${row.label}? Dejará de estar administrado por el Centro. Solo es posible si no tiene actividad competitiva.`
          )
        ) {
          return { ok: false };
        }
        return execute(
          "retire-match",
          ({ idevent, idsport }) => retireCenterMatch({ idevent, idsport, idmatch: row.idmatch }),
          { refreshScope: "structure", successMessage: "Partido retirado del Centro." }
        );
      },

      convertToManual: async (row) =>
        execute(
          "convert-manual",
          ({ idevent, idsport, version }) =>
            convertDraftMatchToManual({
              idevent,
              idsport,
              iddraftmatch: row.iddraftmatch,
              version,
            }),
          { successMessage: "La fila ahora es manual y no volverá a quedar huérfana." }
        ),

      createManual: async (payload) =>
        execute(
          "manual-match",
          ({ idevent, idsport, version }) =>
            createManualMatch({ idevent, idsport, payload, version }),
          { successMessage: "Partido manual agregado al borrador." }
        ),

      resolveParticipants: async (clearMissing = false) =>
        execute(
          "resolve-participants",
          ({ idevent, idsport }) => resolveParticipants({ idevent, idsport, clearMissing }),
          { refreshScope: "structure" }
        ),

      assignSlot: async (payload) =>
        execute(
          "assign-slot",
          ({ idevent, idsport }) => assignParticipantToSlot({ idevent, idsport, payload }),
          { refreshScope: "structure" }
        ),

      saveDraftConfig: async (config) =>
        execute(
          "put-draft",
          ({ idevent, idsport, version }) =>
            putProgrammingCenterDraft({ idevent, idsport, config, version }),
          { refreshScope: "structure" }
        ),

      /** `summaryHash` es el del preview que se está mostrando; nunca uno recalculado aquí. */
      applySync: async ({ config, summaryHash }) =>
        execute(
          "sync-apply",
          ({ idevent, idsport, version }) =>
            applyDraftSync({ idevent, idsport, config, summaryHash, version }),
          { refreshScope: "structure" }
        ),

      materialize: async (options = {}) =>
        execute(
          "materialize",
          ({ idevent, idsport, version }) =>
            materializeDraft({ idevent, idsport, options, version }),
          { refreshScope: "structure" }
        ),

      // ------------------------------- grafo de fases (WG5) -------------------------------
      //
      // Todas refrescan con `scope: "structure"`: una recomputación cambia participantes, así que
      // las filas hidratadas del borrador y el grafo dejan de coincidir con lo que hay en pantalla.

      /** "Actualizar clasificados". `force` viaja lado a lado; nunca hay un "forzar todo". */
      recomputeGraph: async ({ reason = "manual", force = [], forcePositions = false } = {}) =>
        execute(
          "graph-recompute",
          ({ idevent, idsport }) =>
            recomputePhaseGraph({ idevent, idsport, reason, force, forcePositions }),
          { refreshScope: "structure" }
        ),

      /** Bloquea, desbloquea, redefine o quita la fuente de UN lado. */
      setGraphSource: async ({ idmatch, side, reference, locked, clear }) =>
        execute(
          "graph-source",
          ({ idevent, idsport }) =>
            updateGraphSource({ idevent, idsport, idmatch, side, reference, locked, clear }),
          { refreshScope: "structure" }
        ),

      /** "Adoptar en el grafo" un partido de fase final creado fuera del Centro (D-34). */
      adoptIntoGraph: async ({ idmatch, sources }) =>
        execute(
          "graph-adopt",
          ({ idevent, idsport }) => adoptMatchIntoGraph({ idevent, idsport, idmatch, sources }),
          { refreshScope: "structure", successMessage: "Partido adoptado en el grafo de fases." }
        ),

      /** `planHash` y `confirmRemovals` son los del plan aprobado; cambiarlos exige otro preview. */
      applyReorganize: async ({ config, planHash, confirmRemovals }) =>
        execute(
          "reorganize-apply",
          ({ idevent, idsport, version }) =>
            applyReorganization({
              idevent,
              idsport,
              config,
              planHash,
              confirmRemovals,
              version,
            }),
          { refreshScope: "structure" }
        ),
    }),
    [execute, courtsByVenue, dirtyEntries, rows, selection, clearSelection]
  );

export default useProgrammingCenterActions;
